import axiosInstance from "../../../middlewares/axiosInstance";
import {
  APIResponse,
  ListInvoicesRequest,
  ListInvoicesResponse,
  GetApprovedGRNsRequest,
  GetApprovedGRNsResponse,
  InvoiceAutofillRequest,
  InvoiceAutofillResponse,
  ValidateInvoiceNumberRequest,
  ValidateInvoiceNumberResponse,
  CreateInvoiceRequest,
  CreateInvoiceResponse,
  UpdateInvoiceRequest,
  GetInvoiceRequest,
  DeleteInvoiceRequest,
  CheckGRNInvoiceRequest,
  CheckGRNInvoiceResponse,
  ClosePurchaseOrderRequest,
  PurchaseInvoice,
  InvoiceItem,
} from "../types/invoice.types";
import { useAuth } from "../../auth/hooks/useAuth";

export function getBusinessId(
  user?: any,
  vendorDetails?: any,
  grnReference?: any
): string {
  return (
    localStorage.getItem("business_id") ||
    localStorage.getItem("storeId") ||
    user?.business_id ||
    user?.store_id ||
    vendorDetails?.business_id ||
    vendorDetails?.store_id ||
    grnReference?.business_id ||
    grnReference?.store_id ||
    ""
  );
}

class InvoiceService {
  private baseUrl = "/business-admin/invoices";

  // 1. List Invoices
  async listInvoices(
    params: ListInvoicesRequest
  ): Promise<APIResponse<ListInvoicesResponse>> {
    try {
      const response = await axiosInstance.post(`${this.baseUrl}/list`, params);
      return response.data;
    } catch (error: any) {
      console.error("List Invoices API Error:", error);
      throw error;
    }
  }

  // 2. Get Approved GRNs
  async getApprovedGRNs(
    params: GetApprovedGRNsRequest
  ): Promise<APIResponse<GetApprovedGRNsResponse>> {
    try {
      const response = await axiosInstance.post(
        `${this.baseUrl}/approved-grns`,
        params
      );
      return response.data;
    } catch (error: any) {
      console.error("Get Approved GRNs API Error:", error);
      throw error;
    }
  }

  // 3. Get Invoice Autofill Data
  async getInvoiceAutofillData(
    params: InvoiceAutofillRequest
  ): Promise<APIResponse<InvoiceAutofillResponse>> {
    try {
      const response = await axiosInstance.post(
        `${this.baseUrl}/autofill`,
        params
      );
      return response.data;
    } catch (error: any) {
      console.error("Get Invoice Autofill Data API Error:", error);
      throw error;
    }
  }

  // 4. Validate Invoice Number
  async validateInvoiceNumber(
    params: ValidateInvoiceNumberRequest
  ): Promise<APIResponse<ValidateInvoiceNumberResponse>> {
    try {
      const response = await axiosInstance.post(
        `${this.baseUrl}/validate-invoice-number`,
        params
      );
      return response.data;
    } catch (error: any) {
      console.error("Validate Invoice Number API Error:", error);
      throw error;
    }
  }

  // 5. Create Invoice
  async createInvoice(
    data: CreateInvoiceRequest
  ): Promise<APIResponse<CreateInvoiceResponse>> {
    try {
      console.log(
        "🚀 Creating Invoice with data:",
        JSON.stringify(data, null, 2)
      );
      console.log("📍 API endpoint:", `${this.baseUrl}/create`);

      const response = await axiosInstance.post(`${this.baseUrl}/create`, data);

      console.log("✅ Invoice Create API response:", response.data);
      return response.data;
    } catch (error: any) {
      console.error("💥 Invoice Create API Error:", error);
      console.error("💥 Error response:", error.response?.data);
      console.error("💥 Error status:", error.response?.status);
      console.error("💥 Error headers:", error.response?.headers);
      throw error;
    }
  }

  // 6. Update Invoice
  async updateInvoice(
    data: UpdateInvoiceRequest
  ): Promise<APIResponse<{ message: string }>> {
    try {
      const response = await axiosInstance.post(`${this.baseUrl}/update`, data);
      return response.data;
    } catch (error: any) {
      console.error("Update Invoice API Error:", error);
      throw error;
    }
  }

  // 7. Get Single Invoice
  async getInvoice(
    params: GetInvoiceRequest
  ): Promise<APIResponse<PurchaseInvoice>> {
    try {
      const response = await axiosInstance.post(`${this.baseUrl}/get`, params);
      return response.data;
    } catch (error: any) {
      console.error("Get Invoice API Error:", error);
      throw error;
    }
  }

  // 8. Delete Invoice
  async deleteInvoice(
    params: DeleteInvoiceRequest
  ): Promise<APIResponse<{ message: string }>> {
    try {
      const response = await axiosInstance.post(
        `${this.baseUrl}/delete`,
        params
      );
      return response.data;
    } catch (error: any) {
      console.error("Delete Invoice API Error:", error);
      throw error;
    }
  }

  // 9. Check GRN Invoice Status
  async checkGRNInvoiceStatus(
    params: CheckGRNInvoiceRequest
  ): Promise<APIResponse<CheckGRNInvoiceResponse>> {
    try {
      const response = await axiosInstance.post(
        `${this.baseUrl}/check-grn-invoice`,
        params
      );
      return response.data;
    } catch (error: any) {
      console.error("Check GRN Invoice Status API Error:", error);
      throw error;
    }
  }

  // 10. Close Purchase Order
  async closePurchaseOrder(
    params: ClosePurchaseOrderRequest
  ): Promise<APIResponse<{ message: string }>> {
    try {
      const response = await axiosInstance.post(
        "/business-admin/po/close",
        params
      );
      return response.data;
    } catch (error: any) {
      console.error("Close Purchase Order API Error:", error);
      throw error;
    }
  }

  // Helper Methods

  // Calculate invoice totals
  calculateInvoiceTotals(
    items: InvoiceItem[],
    globalTaxRate = 0,
    globalDiscountPercentage = 0,
    globalDiscountAmount = 0,
    taxInclusive = false
  ): {
    subtotal: number;
    totalTaxAmount: number;
    totalDiscountAmount: number;
    grandTotal: number;
  } {
    let subtotal = 0;
    let totalTaxAmount = 0;
    let totalDiscountAmount = 0;

    // Calculate item-level totals
    items.forEach((item) => {
      const lineTotal = item.quantity_invoiced * item.unit_price;
      subtotal += lineTotal;

      // Calculate item-level discount
      let itemDiscountAmount = 0;
      if (item.discount_percentage && item.discount_percentage > 0) {
        itemDiscountAmount = (lineTotal * item.discount_percentage) / 100;
      } else if (item.discount_amount && item.discount_amount > 0) {
        itemDiscountAmount = item.discount_amount;
      }
      totalDiscountAmount += itemDiscountAmount;

      // Calculate tax on discounted amount
      const taxableAmount = lineTotal - itemDiscountAmount;
      const itemTaxRate = item.tax_rate || globalTaxRate;

      if (itemTaxRate && itemTaxRate > 0) {
        let itemTaxAmount = 0;
        if (taxInclusive) {
          // If tax inclusive, extract tax from the price
          itemTaxAmount = (taxableAmount * itemTaxRate) / (100 + itemTaxRate);
        } else {
          // If tax exclusive, add tax to the price
          itemTaxAmount = (taxableAmount * itemTaxRate) / 100;
        }
        totalTaxAmount += itemTaxAmount;
      }
    });

    // Apply global discount
    let globalDiscount = 0;
    if (globalDiscountPercentage > 0) {
      globalDiscount = (subtotal * globalDiscountPercentage) / 100;
    } else if (globalDiscountAmount > 0) {
      globalDiscount = globalDiscountAmount;
    }
    totalDiscountAmount += globalDiscount;

    // Calculate grand total
    let grandTotal = 0;
    if (taxInclusive) {
      // If tax inclusive, subtotal already includes tax
      grandTotal = subtotal - totalDiscountAmount;
    } else {
      // If tax exclusive, add tax to get grand total
      grandTotal = subtotal - totalDiscountAmount + totalTaxAmount;
    }

    return {
      subtotal,
      totalTaxAmount,
      totalDiscountAmount,
      grandTotal: Math.max(0, grandTotal), // Ensure non-negative
    };
  }

  // Generate invoice number suggestion
  generateInvoiceNumberSuggestion(vendorName: string): string {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const vendorPrefix = vendorName
      .substring(0, 3)
      .toUpperCase()
      .replace(/[^A-Z]/g, "X"); // Replace non-letters with X
    const timestamp = Date.now().toString().slice(-4);

    return `INV-${vendorPrefix}-${year}${month}-${timestamp}`;
  }

  // Validate invoice data
  validateInvoiceData(data: CreateInvoiceRequest): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // Basic validations
    if (!data.business_id) {
      errors.push("Business ID is required");
    }

    if (!data.invoice_data.invoice_number?.trim()) {
      errors.push("Invoice number is required");
    }

    if (!data.invoice_data.invoice_date) {
      errors.push("Invoice date is required");
    }

    if (data.invoice_data.invoice_date > Date.now()) {
      errors.push("Invoice date cannot be in the future");
    }

    if (
      data.invoice_data.due_date &&
      data.invoice_data.due_date < data.invoice_data.invoice_date
    ) {
      errors.push("Due date must be after or equal to invoice date");
    }

    if (!data.invoice_data.vendor_details?.vendor_id) {
      errors.push("Vendor details are required");
    }

    if (!data.invoice_data.grn_references?.length) {
      errors.push("At least one GRN reference is required");
    }

    if (!data.invoice_data.items?.length) {
      errors.push("At least one invoice item is required");
    }

    // Validate items
    data.invoice_data.items?.forEach((item, index) => {
      if (!item.item_id) {
        errors.push(`Item ${index + 1}: Item ID is required`);
      }
      if (!item.quantity_invoiced || item.quantity_invoiced <= 0) {
        errors.push(`Item ${index + 1}: Quantity must be greater than 0`);
      }
      if (!item.unit_price || item.unit_price < 0) {
        errors.push(`Item ${index + 1}: Unit price must be 0 or greater`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  // Format currency for display
  formatCurrency(amount: number, currency = "USD"): string {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount);
  }
}

export default new InvoiceService();
